import * as R from 'ramda'
import { RSAA } from 'redux-api-middleware'
import {
  getApiPath,
  makeQueryParams,
  webStorage,
} from 'sidecar-utility-library'

import { types, initialState } from './types'

export const fetchSettings = () => ({
  [RSAA]: {
    endpoint: getApiPath(
      `ntc`,
      `settings?memberUuid=${webStorage.getItem('sub')}`,
    ),
    method: 'GET',
    types: [
      types.FETCH_SETTINGS_REQUEST,
      types.FETCH_SETTINGS_SUCCESS,
      types.FETCH_SETTINGS_ERROR,
    ],
  },
})

export const updateSettings = settings => ({
  [RSAA]: {
    endpoint: getApiPath(
      `ntc`,
      `settings?memberUuid=${webStorage.getItem('sub')}`,
    ),
    method: 'PATCH',
    body: JSON.stringify(settings),
    types: [
      types.UPDATE_SETTINGS_REQUEST,
      types.UPDATE_SETTINGS_SUCCESS,
      types.UPDATE_SETTINGS_ERROR,
    ],
  },
})

export const fetchOutstandingExpenses = memberUuid => ({
  [RSAA]: {
    endpoint: getApiPath(
      `exp`,
      `expenses/search?accountUuid=${webStorage.getItem(
        'aud',
      )}&statusList=OPEN_PENDING_PAYMENT`,
    ),
    method: 'GET',
    types: [
      types.FETCH_OUTSTANDING_EXPENSES_REQUEST,
      {
        type: types.FETCH_OUTSTANDING_EXPENSES_SUCCESS,
        meta: { memberUuid },
      },
      types.FETCH_OUTSTANDING_EXPENSES_ERROR,
    ],
  },
})

export const initializeOutstandingExpenses = () => ({
  type: types.INITIALIZE_OUTSTANDING_EXPENSES,
})

export const toggleAutopaymentExpense = autopaymentExpense => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `subscriptions/expenses/autopayment${makeQueryParams({
        accountUuid: webStorage.getItem('aud'),
        autopaymentExpense,
      })}`,
    ),
    method: 'PUT',
    types: [
      types.TOGGLE_AUTOPAYMENT_EXPENSE_REQUEST,
      types.TOGGLE_AUTOPAYMENT_EXPENSE_SUCCESS,
      types.TOGGLE_AUTOPAYMENT_EXPENSE_ERROR,
    ],
  },
})

export const fetchAutopaymentExpense = () => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `subscriptions/expenses/autopayment${makeQueryParams({
        accountUuid: webStorage.getItem('aud'),
      })}`,
    ),
    method: 'GET',
    types: [
      types.FETCH_AUTOPAYMENT_EXPENSE_REQUEST,
      types.FETCH_AUTOPAYMENT_EXPENSE_SUCCESS,
      types.FETCH_AUTOPAYMENT_EXPENSE_ERROR,
    ],
  },
})

export const mailCheck = payload => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `expenses/send-credits?memberUuid=${
        payload.uuid || webStorage.getItem('sub')
      }`,
    ),
    method: 'PUT',
    body: JSON.stringify({
      name: payload.payableTo,
      street: payload.street,
      street2: payload.street2,
      city: payload.city,
      state: payload.state,
      zip: payload.zip,
    }),
    types: [
      types.MAIL_CHECK_REQUEST,
      types.MAIL_CHECK_SUCCESS,
      types.MAIL_CHECK_ERROR,
    ],
  },
})

export const fetchAccountMetaData = () => {
  const uuid = webStorage.getItem('aud')
  return {
    [RSAA]: {
      endpoint: getApiPath('acct', `accounts/${uuid}/account-metadata`),
      method: 'GET',
      types: [
        types.FETCH_ACCOUNT_METADATA_REQUEST,
        types.FETCH_ACCOUNT_METADATA_SUCCESS,
        types.FETCH_ACCOUNT_METADATA_ERROR,
      ],
    },
  }
}

export const fetchFinancialAccountBalance = (newEndpoint = false) => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      newEndpoint
        ? `financialAccount/accounts${makeQueryParams({
            accountUuid: webStorage.getItem('aud'),
          })}`
        : `financialAccount/accountview${makeQueryParams({
            accountUuid: webStorage.getItem('aud'),
          })}`,
    ),
    method: 'GET',
    types: [
      types.FETCH_FINANCIAL_ACCOUNT_BALANCE_REQUEST,
      types.FETCH_FINANCIAL_ACCOUNT_BALANCE_SUCCESS,
      types.FETCH_FINANCIAL_ACCOUNT_BALANCE_ERROR,
    ],
  },
})

export const fetchMoneyBackSummary = () => {
  const memberUuid = webStorage.getItem('sub')

  return {
    [RSAA]: {
      endpoint: getApiPath(
        'exp',
        `financialAccount/${memberUuid}/money-back-summary`,
      ),
      method: 'GET',
      types: [
        types.FETCH_MONEY_BACK_SUMMARY_REQUEST,
        types.FETCH_MONEY_BACK_SUMMARY_SUCCESS,
        types.FETCH_MONEY_BACK_SUMMARY_ERROR,
      ],
    },
  }
}

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.INITIALIZE_OUTSTANDING_EXPENSES:
      return { ...state, outstandingExpenses: initialState.outstandingExpenses }
    case types.FETCH_OUTSTANDING_EXPENSES_REQUEST:
      return {
        ...state,
        outstandingExpenses: {
          ...state.outstandingExpenses,
          busy: true,
          error: null,
        },
      }
    case types.FETCH_OUTSTANDING_EXPENSES_SUCCESS:
      return {
        ...state,
        outstandingExpenses: {
          ...state.outstandingExpenses,
          entities: R.pipe(
            R.propOr([], 'content'),
            R.filter(({ memberUuid }) => meta.memberUuid === memberUuid),
          )(payload),
          busy: false,
        },
      }
    case types.FETCH_OUTSTANDING_EXPENSES_ERROR:
      return {
        ...state,
        outstandingExpenses: {
          ...state.outstandingExpenses,
          busy: false,
          error: R.pathOr(
            'There has been an error',
            ['response', 'message'],
            payload,
          ),
        },
      }
    case types.TOGGLE_AUTOPAYMENT_EXPENSE_REQUEST:
    case types.FETCH_AUTOPAYMENT_EXPENSE_REQUEST: {
      return {
        ...state,
        autopaymentExpense: {
          ...state.autopaymentExpense,
          busy: true,
          error: null,
        },
      }
    }
    case types.TOGGLE_AUTOPAYMENT_EXPENSE_SUCCESS:
    case types.FETCH_AUTOPAYMENT_EXPENSE_SUCCESS: {
      return {
        ...state,
        autopaymentExpense: {
          ...state.autopaymentExpense,
          busy: false,
          entity: payload.autopaymentExpense,
        },
      }
    }
    case types.TOGGLE_AUTOPAYMENT_EXPENSE_ERROR:
    case types.FETCH_AUTOPAYMENT_EXPENSE_ERROR: {
      return {
        ...state,
        autopaymentExpense: {
          ...state.autopaymentExpense,
          busy: false,
          error: R.pathOr(
            'There has been an error',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }
    case types.FETCH_SETTINGS_REQUEST:
    case types.UPDATE_SETTINGS_REQUEST: {
      return {
        ...state,
        notificationSettings: {
          ...state.notificationSettings,
          fetching: true,
          error: null,
        },
      }
    }
    case types.FETCH_SETTINGS_SUCCESS:
    case types.UPDATE_SETTINGS_SUCCESS: {
      return {
        ...state,
        notificationSettings: {
          ...state.notificationSettings,
          fetching: false,
          entities: payload,
        },
      }
    }
    case types.FETCH_SETTINGS_ERROR:
    case types.UPDATE_SETTINGS_ERROR: {
      return {
        ...state,
        notificationSettings: {
          ...state.notificationSettings,
          fetching: false,
          error: payload.response.message,
        },
      }
    }
    case types.MAIL_CHECK_REQUEST:
      return {
        ...state,
        mailCheck: {
          ...state.mailCheck,
          busy: true,
          error: null,
        },
      }
    case types.MAIL_CHECK_SUCCESS:
      return {
        ...state,
        mailCheck: {
          ...state.mailCheck,
          busy: false,
        },
      }
    case types.MAIL_CHECK_ERROR:
      return {
        ...state,
        mailCheck: {
          ...state.mailCheck,
          busy: false,
          error: payload.response.message,
        },
      }
    case types.FETCH_ACCOUNT_METADATA_REQUEST:
      return {
        ...state,
        accountMetadata: {
          ...state.accountMetadata,
          busy: true,
          error: '',
        },
      }
    case types.FETCH_ACCOUNT_METADATA_SUCCESS:
      return {
        ...state,
        accountMetadata: {
          ...state.accountMetadata,
          busy: false,
          entity: payload,
        },
      }
    case types.FETCH_ACCOUNT_METADATA_ERROR:
      return {
        ...state,
        accountMetadata: {
          ...state.accountMetadata,
          busy: false,
          error: payload.response.message,
        },
      }
    case types.FETCH_FINANCIAL_ACCOUNT_BALANCE_REQUEST:
      return {
        ...state,
        financialBalance: {
          ...state.financialBalance,
          busy: true,
          error: '',
        },
      }
    case types.FETCH_FINANCIAL_ACCOUNT_BALANCE_SUCCESS:
      return {
        ...state,
        financialBalance: {
          ...state.financialBalance,
          busy: false,
          entities: payload,
        },
      }
    case types.FETCH_FINANCIAL_ACCOUNT_BALANCE_ERROR:
      return {
        ...state,
        financialBalance: {
          ...state.financialBalance,
          busy: false,
          error: payload.response.message,
        },
      }

    case types.FETCH_MONEY_BACK_SUMMARY_REQUEST:
      return {
        ...state,
        moneyBackSummary: {
          ...state.moneyBackSummary,
          busy: true,
          error: '',
        },
      }
    case types.FETCH_MONEY_BACK_SUMMARY_SUCCESS:
      return {
        ...state,
        moneyBackSummary: {
          ...state.moneyBackSummary,
          busy: false,
          entity: payload,
        },
      }
    case types.FETCH_MONEY_BACK_SUMMARY_ERROR:
      return {
        ...state,
        moneyBackSummary: {
          ...state.moneyBackSummary,
          busy: false,
          error: payload.response.message,
        },
      }

    default:
      return state
  }
}
